<template>
    <div id="product-info" class="flex h-fit w-full flex-col lg:sticky lg:top-4 lg:w-2/5">
        <Panel>
            <!-- Visual copy of the title on desktop; the page's only h1 is MobileProductTitle at the top. -->
            <p
                aria-hidden="true"
                data-test-id="pdp-title"
                :class="['mt-0 font-semibold wrap-break-word max-lg:hidden', titleSize]"
                v-text="productPageStore.configurableProduct.name"
            />

            <!-- Product reviews -->
            <ProductReviews :review-score="reviewScore" :review-count="reviewCount" />
            <TrustedShopsProductTitleWidgets v-if="!isMounted || isDesktop" class="mt-2 max-lg:hidden" />

            <!-- Render product switch options -->
            <ProductSwitchOptions />

            <!-- Set size selector -->
            <ProductSetPositions v-if="productStore.isSetProduct" />

            <!-- Product filters. -->
            <ProductFilters v-else-if="!productStore.isPanelSetProduct" />

            <!-- Render in stock -->
            <ProductInStock />

            <!-- Render product price -->
            <ProductSetPrice v-if="productStore.isSetBackedProduct" />
            <ProductPrice v-else />

            <!-- Render product delivery information -->
            <ProductDeliveryInformation />

            <!-- Render product button -->
            <ProductButton />

            <!-- Render Klarna promotion badge -->
            <ProductKlarna :price="klarnaPrice" />

            <!-- Render discount blocks below the Klarna OSM block -->
            <DiscountBlocks :blocks="productPageStore.discountBlocks" />

            <!-- Render product timer (only on mobile) -->
            <ProductTimer />

            <!-- Render product USPs -->
            <ProductUsps />
        </Panel>

        <ProductShippingOptions class="mt-4" />
    </div>
</template>

<script setup lang="ts">
import DiscountBlocks from '@/components/discount-blocks/DiscountBlocks.vue';
import Panel from '@/components/panels/Panel.vue';
import TrustedShopsProductTitleWidgets from '@/components/reviews/TrustedShopsProductTitleWidgets.vue';
import { useAppearanceComposable } from '@/composables/appearance/AppearanceComposable';
import { useIsMounted } from '@/composables/useIsMounted';
import ProductButton from '@/pages/product-page/partials/product-info/partials/ProductButton.vue';
import ProductDeliveryInformation from '@/pages/product-page/partials/product-info/partials/ProductDeliveryInformation.vue';
import ProductFilters from '@/pages/product-page/partials/product-info/partials/ProductFilters.vue';
import ProductInStock from '@/pages/product-page/partials/product-info/partials/ProductInStock.vue';
import ProductKlarna from '@/pages/product-page/partials/product-info/partials/ProductKlarna.vue';
import ProductPrice from '@/pages/product-page/partials/product-info/partials/ProductPrice.vue';
import ProductReviews from '@/pages/product-page/partials/product-info/partials/ProductReviews.vue';
import ProductSetPositions from '@/pages/product-page/partials/product-info/partials/set/ProductSetPositions.vue';
import ProductSetPrice from '@/pages/product-page/partials/product-info/partials/set/ProductSetPrice.vue';
import ProductShippingOptions from '@/pages/product-page/partials/product-info/partials/ProductShippingOptions.vue';
import ProductSwitchOptions from '@/pages/product-page/partials/product-info/partials/ProductSwitchOptions.vue';
import ProductTimer from '@/pages/product-page/partials/product-info/partials/ProductTimer.vue';
import ProductUsps from '@/pages/product-page/partials/product-info/partials/ProductUsps.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductSetStore } from '@/stores/products/ProductSetStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { computed } from 'vue';

type ProductInfoProps = {
    reviewScore?: number;
    reviewCount?: number;
};

defineProps<ProductInfoProps>();
const { isDesktop } = useAppearanceComposable();
const isMounted = useIsMounted();
const productPageStore = useProductPageStore();
const productStore = useProductStore();
const setStore = useProductSetStore();
const titleSize = computed(() => (productPageStore.configurableProduct.name.length > 21 ? 'text-2xl' : 'text-3xl'));

// A set's default variant is only its first position, so that variant's price
// would understate what Klarna is asked to spread.
const klarnaPrice = computed(() => (productStore.isSetBackedProduct ? setStore.total : productStore.selectedProduct.product.data.product_price));
</script>
